/**
 * EnsoMirai++ Autonomous OS — Runtime Event Bus Store
 *
 * Owns the single project WebSocket (`/ws/projects/:id/insights`). Every
 * frame is fanned out to {@link subscribeRuntimeEvents} listeners (raw, before
 * any filtering) so feature hooks share one connection instead of opening
 * their own; the store itself maps engineering/ops events onto the timeline,
 * agent streams and terminal buffers, and invalidates the TanStack queries
 * that render them.
 */

import { create } from 'zustand';
import { QueryClient, type QueryKey } from '@tanstack/react-query';
import type { EngineeringWsMessage } from '../types/engineering';
import {
  amsInboxKey,
  projectAmsKey,
  projectIncidentsKey,
  skillExecutionsKey,
  understandingInsightsKey,
  understandingStatusKey,
  workspaceMemoryKey,
  wsCredentialQuery,
} from '../services/api';
import { nextWsRetryMs } from '../hooks/wsBackoff';

function wsOrigin(): string {
  const configured = import.meta.env.VITE_WS_ORIGIN;
  if (configured) return String(configured);
  if (typeof window === 'undefined') return 'ws://localhost';
  return `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;
}

export interface AgentState {
  reasoning?: string;
  task?: string;
  status?: string;
  confidence?: number;
}

/** Raw frame as sent by the backend hub (before any event-type filtering). */
export interface RuntimeWsEnvelope {
  type: string;
  payload?: Record<string, unknown>;
  timestamp?: string;
}

export interface RuntimeEventState {
  // Connection states
  connected: boolean;
  activeProjectId: string | null;

  // Real-time artifact feeds
  timeline: EngineeringWsMessage[];
  agentStreams: Record<string, AgentState>;
  terminalLogs: Record<string, string[]>; // Maps taskID -> stdout line buffers
  activePlanSteps: Record<string, string>; // Maps taskID -> current executing step

  // Actions
  connect: (projectId: string, queryClient: QueryClient) => void;
  disconnect: () => void;
  clearLogs: (taskId: string) => void;
}

const OPS_WS_PREFIXES = [
  'operational_event.',
  'mission_decision.',
  'mission.',
  'deployment.',
  'incident.',
];

const ENGINEERING_EVENT_PREFIXES = [
  'executor.',
  'planner.',
  'orchestrator.',
  'validation.',
  'mutation.',
  'engineering.',
  'session.',
  'agent.',
  'artifact.',
  'changeset.',
];

const WORKSPACE_MEMORY_PREFIX = 'workspace_memory.';
const UNDERSTANDING_PREFIXES = ['understanding.', 'worker.', 'insight.'];

function isProjectInsightEvent(type: string): boolean {
  const t = type.toLowerCase();
  const prefixes = [...ENGINEERING_EVENT_PREFIXES, ...OPS_WS_PREFIXES];
  return prefixes.some((p) => t.startsWith(p) || t.includes(p.replace('.', '')));
}

/** Query families one event type makes stale. */
export function runtimeEventInvalidationKeys(projectId: string, eventType: string): QueryKey[] {
  const t = eventType.toLowerCase();
  if (OPS_WS_PREFIXES.some((p) => t.startsWith(p))) {
    return [
      amsInboxKey(),
      projectAmsKey(projectId),
      projectIncidentsKey(projectId),
      skillExecutionsKey(projectId),
      understandingInsightsKey(projectId),
    ];
  }
  if (t.startsWith(WORKSPACE_MEMORY_PREFIX)) {
    return [workspaceMemoryKey(projectId)];
  }
  if (UNDERSTANDING_PREFIXES.some((p) => t.startsWith(p))) {
    return [understandingInsightsKey(projectId), understandingStatusKey(projectId)];
  }
  return [];
}

/** Query invalidation for one event type, applied immediately. */
export function invalidateForRuntimeEvent(queryClient: QueryClient, projectId: string, eventType: string): void {
  for (const queryKey of runtimeEventInvalidationKeys(projectId, eventType)) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

/** Quiet period after the last event before a burst's invalidations flush. */
export const INVALIDATION_DEBOUNCE_MS = 250;
/** Upper bound on how long a steady stream of events can defer a flush. */
export const INVALIDATION_MAX_WAIT_MS = 1_000;

/**
 * Coalesces socket invalidations: a burst of events marks each query family
 * stale once, after the stream goes quiet for INVALIDATION_DEBOUNCE_MS (or at
 * most INVALIDATION_MAX_WAIT_MS after the first event), instead of refetching
 * every family on every frame. `flush()` applies anything pending right away.
 */
export function createInvalidationBatcher(
  queryClient: QueryClient,
  { debounceMs = INVALIDATION_DEBOUNCE_MS, maxWaitMs = INVALIDATION_MAX_WAIT_MS } = {},
) {
  const pending = new Map<string, QueryKey>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let firstAt = 0;

  const flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    const keys = [...pending.values()];
    pending.clear();
    for (const queryKey of keys) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };

  const add = (keys: QueryKey[]) => {
    if (!keys.length) return;
    if (!pending.size) firstAt = Date.now();
    for (const key of keys) pending.set(JSON.stringify(key), key);
    if (timer) clearTimeout(timer);
    const wait = Math.max(0, Math.min(debounceMs, firstAt + maxWaitMs - Date.now()));
    timer = setTimeout(flush, wait);
  };

  return { add, flush };
}

let invalidations: ReturnType<typeof createInvalidationBatcher> | null = null;

/** Parse a socket frame into the normalized envelope; null when unparseable. */
export function parseRuntimeFrame(data: unknown): RuntimeWsEnvelope | null {
  try {
    const raw = JSON.parse(String(data)) as {
      type?: string;
      payload?: Record<string, unknown>;
      timestamp?: string;
    };
    if (!raw || typeof raw !== 'object') return null;
    return { type: raw.type ?? '', payload: raw.payload, timestamp: raw.timestamp };
  } catch {
    return null;
  }
}

type RuntimeListener = (frame: RuntimeWsEnvelope) => void;
const listeners = new Set<RuntimeListener>();

/**
 * Subscribe to every raw frame of the shared project socket. Returns the
 * unsubscribe function. Listeners receive frames for whichever project the
 * store is currently connected to — check `activeProjectId` if that matters.
 */
export function subscribeRuntimeEvents(listener: RuntimeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function fanOut(frame: RuntimeWsEnvelope): void {
  for (const listener of listeners) {
    try {
      listener(frame);
    } catch {
      /* one bad subscriber must not break the others */
    }
  }
}

let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;

export const useRuntimeEventStore = create<RuntimeEventState>((set, get) => ({
  connected: false,
  activeProjectId: null,
  timeline: [],
  agentStreams: {},
  terminalLogs: {},
  activePlanSteps: {},

  connect: (projectId, queryClient) => {
    // If already connected to the same project, do nothing
    if (get().activeProjectId === projectId && socket && socket.readyState === WebSocket.OPEN) {
      return;
    }

    // Clean up any existing connection
    get().disconnect();

    set({ activeProjectId: projectId, timeline: [], agentStreams: {}, terminalLogs: {}, activePlanSteps: {} });

    const batcher = createInvalidationBatcher(queryClient);
    invalidations = batcher;

    const url = `${wsOrigin()}/ws/projects/${encodeURIComponent(projectId)}/insights${wsCredentialQuery()}`;

    const establishConnection = () => {
      if (get().activeProjectId !== projectId) return;

      socket = new WebSocket(url);

      socket.onopen = () => {
        reconnectAttempt = 0;
        set({ connected: true });
      };

      socket.onmessage = (event) => {
        const frame = parseRuntimeFrame(event.data);
        if (!frame) return;

        // 0. Fan the raw frame out to feature subscribers (workspace memory, review streams, …)
        fanOut(frame);

        const inner = frame.payload;
        const engineeringEvent =
          typeof inner?.engineering_event === 'string' ? inner.engineering_event : frame.type;

        // Query invalidation happens for every recognised prefix, whether or
        // not the event also lands on the visual timeline. Batched so a burst
        // refetches each family once.
        batcher.add(runtimeEventInvalidationKeys(projectId, engineeringEvent));

        if (!isProjectInsightEvent(engineeringEvent)) return;

        const msg: EngineeringWsMessage = {
          type: engineeringEvent,
          payload: inner?.payload ? (inner.payload as Record<string, unknown>) : inner,
          timestamp: frame.timestamp ?? new Date().toISOString(),
        };

        // Update timeline feed
        set((state) => ({
          timeline: [msg, ...state.timeline].slice(0, 200),
        }));

        const payload = msg.payload ?? {};

        // 1. Process Swarm Agent thought streams
        const role = String(payload.agent_role ?? payload.role ?? '');
        if (role) {
          set((state) => {
            const currentAgent = state.agentStreams[role] ?? {};
            return {
              agentStreams: {
                ...state.agentStreams,
                [role]: {
                  reasoning: String(payload.reasoning ?? payload.detail ?? currentAgent.reasoning ?? ''),
                  task: String(payload.target ?? payload.task ?? currentAgent.task ?? ''),
                  status: msg.type,
                  confidence: typeof payload.confidence === 'number' ? payload.confidence : currentAgent.confidence,
                },
              },
            };
          });
        }

        // 2. Process Terminal Logs (xterm.js logs)
        const taskId = String(payload.task_id ?? payload.task ?? '');
        const stdoutLine = String(payload.stdout ?? payload.log ?? payload.output ?? '');
        if (taskId && stdoutLine) {
          set((state) => {
            const existingLogs = state.terminalLogs[taskId] ?? [];
            return {
              terminalLogs: {
                ...state.terminalLogs,
                [taskId]: [...existingLogs, stdoutLine],
              },
            };
          });
        }

        // 3. Process Active Planning step changes
        const currentStep = String(payload.current_step ?? payload.step ?? '');
        if (taskId && currentStep) {
          set((state) => ({
            activePlanSteps: {
              ...state.activePlanSteps,
              [taskId]: currentStep,
            },
          }));
        }
      };

      socket.onclose = () => {
        // Events already received must still reach the cache.
        batcher.flush();
        set({ connected: false });
        if (get().activeProjectId === projectId) {
          reconnectTimer = setTimeout(establishConnection, nextWsRetryMs(reconnectAttempt));
          reconnectAttempt += 1;
        }
      };

      socket.onerror = () => {
        if (socket) socket.close();
      };
    };

    establishConnection();
  },

  disconnect: () => {
    // Apply invalidations still waiting on the debounce instead of dropping them.
    invalidations?.flush();
    invalidations = null;
    set({ connected: false, activeProjectId: null });
    reconnectAttempt = 0;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (socket) {
      socket.close();
      socket = null;
    }
  },

  clearLogs: (taskId) => {
    set((state) => {
      const copy = { ...state.terminalLogs };
      delete copy[taskId];
      return { terminalLogs: copy };
    });
  },
}));
